import { useQuery } from "@tanstack/react-query";
import { Banknote, ClipboardList, FilePlus2, Pencil, Trash2, Undo2 } from "lucide-react";
import { formatCents } from "@shared/money";
import { errorMessage } from "@web/lib/api";
import { fmtStamp, humanize } from "@web/lib/format";
import { bookingActivityQuery, membersQuery, type ActivityRow } from "@web/lib/queries";
import { Button, EmptyState, Skeleton, Stack, TimelineItem } from "@web/ui";

const MONEY_KEYS = new Set(["total", "subtotal", "discountTotal", "paidTotal", "balanceDue", "depositAmount", "amount", "unitAmount", "damageAmount"]);
const parse = (s: string | null): Record<string, unknown> => {
  if (!s) return {};
  try {
    const v = JSON.parse(s) as unknown;
    return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};
const show = (k: string, v: unknown) => (v === null || v === undefined || v === "" ? "—" : MONEY_KEYS.has(k) && typeof v === "number" ? formatCents(v) : typeof v === "object" ? JSON.stringify(v) : String(v));

/** "eventDate: 2027-05-15 → 2027-05-16 · total: $5,000.00 → $5,250.00" (first few changed keys). */
function summary(a: ActivityRow): string {
  const before = parse(a.before);
  const after = parse(a.after);
  const keys = [...new Set([...Object.keys(after), ...Object.keys(before)])].filter((k) => k !== "bookingId" && k !== "id");
  const parts = keys.slice(0, 4).map((k) => (k in before && k in after ? `${k}: ${show(k, before[k])} → ${show(k, after[k])}` : `${k}: ${show(k, after[k] ?? before[k])}`));
  return parts.join(" · ") + (keys.length > 4 ? ` · +${keys.length - 4} more` : "");
}

const icon = (action: string) => {
  if (action.includes("payment")) return <Banknote aria-hidden size={16} />;
  if (action.includes("refund")) return <Undo2 aria-hidden size={16} />;
  if (action === "create") return <FilePlus2 aria-hidden size={16} />;
  if (action === "delete" || action === "cancel") return <Trash2 aria-hidden size={16} />;
  if (action === "update") return <Pencil aria-hidden size={16} />;
  return <ClipboardList aria-hidden size={16} />;
};

/** Activity tab: the booking's audit log (own rows + its lines and payments). */
export function ActivityTab({ bookingId }: { bookingId: string }) {
  const q = useQuery(bookingActivityQuery(bookingId));
  const members = useQuery(membersQuery);
  const who = (a: ActivityRow) => (a.memberId ? (members.data?.find((m) => m.id === a.memberId)?.name ?? "Team member") : (a.actorLabel ?? "System"));
  // An auditor looking for the trail must be told it is missing, not left with a
  // shimmer that never resolves.
  if (q.isError) return <EmptyState icon={ClipboardList} title="Activity could not be loaded" body={errorMessage(q.error)} action={<Button onClick={() => void q.refetch()}>Try again</Button>} />;
  if (!q.data) return <Skeleton count={5} />;
  if (q.data.length === 0) return <EmptyState icon={ClipboardList} title="No activity yet" />;
  return (
    <Stack gap={0}>
      {q.data.map((a, i) => (
        <TimelineItem key={a.id} icon={icon(a.action)} title={`${humanize(a.action)}${a.entityType === "booking" ? "" : ` · ${humanize(a.entityType)}`}`} body={summary(a) || undefined} meta={`${who(a)} · ${fmtStamp(a.at)}`} last={i === q.data.length - 1} />
      ))}
    </Stack>
  );
}
